/**
 * Default time-to-first-byte timeout for CDN fetches.
 * Covers 4G/Wi-Fi + degraded 3G; longer would make users wait too long if CDN
 * is unreachable. Body streaming is NOT subject to this timeout — once
 * response headers arrive, downloads of any size proceed normally.
 */
export const CDN_TTFB_TIMEOUT_MS = 4000;

/**
 * Wraps `fetch()` with a TTFB-only abort timer.
 *
 * - `fetch()` resolves when the response headers are received, NOT when the
 *   body finishes — so this timeout fires only if the server fails to respond
 *   at all (DNS/TCP/TLS/edge unreachable).
 * - Once headers arrive the timer is cleared and the body can stream for any
 *   duration. Slow downloads (3G, slowloris, etc.) are NOT cancelled.
 * - On abort, fetch rejects with an AbortError-like object — caller catches it
 *   like any other fetch failure (e.g. handed to `loadWithCdnFallback`).
 */
export const fetchWithTtfbTimeout = async (
  url: string,
  ttfbMs: number = CDN_TTFB_TIMEOUT_MS,
  init?: RequestInit,
): Promise<Response> => {
  const ac = new AbortController();

  // If caller passed a signal, link it: aborting the outer signal also aborts
  // our internal one (e.g. component unmount cancels in-flight load).
  const externalSignal = init?.signal ?? null;
  const externalAbortHandler = externalSignal
    ? () => ac.abort(externalSignal.reason)
    : null;
  if (externalSignal && externalAbortHandler) {
    if (externalSignal.aborted) {
      ac.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener('abort', externalAbortHandler, { once: true });
    }
  }

  const timer = setTimeout(
    () => ac.abort(new Error(`CDN TTFB timeout after ${ttfbMs}ms: ${url}`)),
    ttfbMs,
  );

  try {
    return await fetch(url, { ...init, signal: ac.signal });
  } finally {
    clearTimeout(timer);
    if (externalSignal && externalAbortHandler) {
      externalSignal.removeEventListener('abort', externalAbortHandler);
    }
  }
};
